import { DIET_MEAL_KINDS, type DietLog, type DietMealKind } from "@couples/shared";
import { Minus, Plus } from "lucide-react";
import { useState } from "react";

import { todayDateKey } from "../lib/dates";
import type { DietLogInput } from "../lib/diet/logs";
import SheetShell from "./SheetShell";

/** 食事欄ごとの入力例 */
const MEAL_PLACEHOLDERS: Record<DietMealKind, string> = {
  mealBreakfast: "例: ゆで卵とヨーグルト",
  mealLunch: "例: 社食の定食",
  mealDinner: "例: 鍋",
  mealSnack: "例: プロテインバー",
};

interface Props {
  /** 書き直す対象。無ければその日の新規記録 */
  editingLog?: DietLog;
  /** 記録する日。`YYYY-MM-DD` */
  date: string;
  /** 日付を変えたとき。その日に自分の記録があれば読み込み直すのは呼び出し側の仕事 */
  onDateChange: (date: string) => void;
  /** ±ボタンの起点。前回の記録が無ければ目標の開始体重を渡す */
  defaultWeight: number;
  busy: boolean;
  onSave: (input: DietLogInput) => void;
  onClose: () => void;
}

const inputClass =
  "w-full rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none";

/** 体重の刻み。体組成計の表示に合わせて 0.1kg */
const STEP = 0.1;

/** 小数の足し引きで 58.60000000000001 を作らないよう、0.1 刻みに丸め直す */
function roundStep(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * その日の記録を書くシート。毎日開くものなので、体重だけ入れて閉じられる並びにする。
 * 体重は入力欄より ± のタップのほうが速いので、前回の値から動かす形を主にしている。
 */
export default function DietLogSheet({ date, onDateChange, onClose, ...formProps }: Props) {
  const today = todayDateKey();
  return (
    <SheetShell
      title={date === today ? "今日の記録" : `${date.replaceAll("-", "/")}の記録`}
      onClose={onClose}
      tone="bg"
    >
      {/* 昨日以前の付け忘れを後から埋められるようにする。未来日は記録できない */}
      <label className="flex items-center gap-2 border-b border-[var(--color-line)] px-4 py-2.5">
        <span className="text-[11px] text-[var(--color-sub)]">日付</span>
        <input
          type="date"
          value={date}
          max={today}
          onChange={(event) => {
            const next = event.target.value;
            // 入力欄を消したときは空文字になる。そのまま渡すと保存先の日付が壊れる
            if (next !== "" && next <= today) onDateChange(next);
          }}
          className="rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-1.5 text-base outline-none"
        />
      </label>
      {/* 日付ごとに作り直して、その日の記録で入力欄を初期化する */}
      <DietLogForm key={date} date={date} {...formProps} />
    </SheetShell>
  );
}

function DietLogForm({
  editingLog,
  date,
  defaultWeight,
  busy,
  onSave,
}: Omit<Props, "onClose" | "onDateChange">) {
  const [weight, setWeight] = useState(roundStep(editingLog?.weight ?? defaultWeight));
  const [bodyFat, setBodyFat] = useState(
    editingLog?.bodyFat === null || editingLog?.bodyFat === undefined
      ? ""
      : String(editingLog.bodyFat),
  );
  const [meals, setMeals] = useState<Record<DietMealKind, string>>({
    mealBreakfast: editingLog?.mealBreakfast ?? "",
    mealLunch: editingLog?.mealLunch ?? "",
    mealDinner: editingLog?.mealDinner ?? "",
    mealSnack: editingLog?.mealSnack ?? "",
  });
  const [calories, setCalories] = useState<Record<DietMealKind, string>>({
    mealBreakfast: editingLog?.calorieBreakfast == null ? "" : String(editingLog.calorieBreakfast),
    mealLunch: editingLog?.calorieLunch == null ? "" : String(editingLog.calorieLunch),
    mealDinner: editingLog?.calorieDinner == null ? "" : String(editingLog.calorieDinner),
    mealSnack: editingLog?.calorieSnack == null ? "" : String(editingLog.calorieSnack),
  });
  const [exercise, setExercise] = useState(editingLog?.exercise ?? "");
  const [note, setNote] = useState(editingLog?.note ?? "");

  const fatValue = bodyFat.trim() === "" ? null : Number(bodyFat);
  const fatValid =
    fatValue === null || (Number.isFinite(fatValue) && fatValue > 0 && fatValue < 100);

  const calorieValueOf = (key: DietMealKind): number | null => {
    const raw = calories[key].trim();
    return raw === "" ? null : Number(raw);
  };
  const calorieValidOf = (key: DietMealKind): boolean => {
    const value = calorieValueOf(key);
    return value === null || (Number.isFinite(value) && value > 0 && value < 10000);
  };
  const caloriesValid = DIET_MEAL_KINDS.every(({ key }) => calorieValidOf(key));
  const calorieTotal = DIET_MEAL_KINDS.reduce<number | null>((sum, { key }) => {
    const value = calorieValueOf(key);
    if (value === null) return sum;
    return (sum ?? 0) + value;
  }, null);

  const canSubmit = weight > 0 && weight < 500 && fatValid && caloriesValid;

  const trimmed = (value: string) => (value.trim() === "" ? null : value.trim());

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    onSave({
      loggedOn: date,
      weight,
      bodyFat: fatValue,
      mealBreakfast: trimmed(meals.mealBreakfast),
      mealLunch: trimmed(meals.mealLunch),
      mealDinner: trimmed(meals.mealDinner),
      mealSnack: trimmed(meals.mealSnack),
      calorieBreakfast: calorieValueOf("mealBreakfast"),
      calorieLunch: calorieValueOf("mealLunch"),
      calorieDinner: calorieValueOf("mealDinner"),
      calorieSnack: calorieValueOf("mealSnack"),
      exercise: trimmed(exercise),
      note: trimmed(note),
    });
  }

  return (
    <>
      <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto p-4">
          {/* 体重だけは大きく置く。毎日必ず触るのはここだけ */}
          <div className="flex items-center justify-between rounded-xl border border-[var(--color-line)] bg-white px-4 py-4">
            <button
              type="button"
              onClick={() => {
                setWeight((current) => roundStep(Math.max(STEP, current - STEP)));
              }}
              aria-label="0.1kg 減らす"
              className="flex size-11 items-center justify-center rounded-full border border-[var(--color-line)] transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)]"
            >
              <Minus className="size-4 text-[var(--color-sub)]" aria-hidden />
            </button>

            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold tabular-nums">{weight.toFixed(1)}</span>
              <span className="text-xs text-[var(--color-sub)]">kg</span>
            </div>

            <button
              type="button"
              onClick={() => {
                setWeight((current) => roundStep(current + STEP));
              }}
              aria-label="0.1kg 増やす"
              className="flex size-11 items-center justify-center rounded-full border border-[var(--color-line)] transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)]"
            >
              <Plus className="size-4 text-[var(--color-sub)]" aria-hidden />
            </button>
          </div>

          <label className="block">
            <span className="mb-1 block text-[11px] text-[var(--color-sub)]">体重(kg)</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              min="1"
              max="499"
              value={weight}
              onChange={(event) => {
                const next = Number(event.target.value);
                if (Number.isFinite(next)) setWeight(next);
              }}
              className={inputClass}
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-[11px] text-[var(--color-sub)]">体脂肪率(任意)</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              value={bodyFat}
              onChange={(event) => {
                setBodyFat(event.target.value);
              }}
              placeholder="例: 22.4"
              className={inputClass}
            />
            {!fatValid && (
              <span className="mt-1 block text-[11px] text-[var(--color-plum)]">
                体脂肪率は0より大きく100未満で入れてください。
              </span>
            )}
          </label>

          <div className="flex flex-col gap-2.5">
            <span className="flex items-baseline justify-between text-[11px] text-[var(--color-sub)]">
              食べたもの・カロリー(任意)
              {calorieTotal !== null && (
                <span className="tabular-nums">合計 {calorieTotal}kcal</span>
              )}
            </span>
            {DIET_MEAL_KINDS.map(({ key, label }) => (
              <div key={key} className="flex flex-col gap-1.5">
                <label className="flex items-center gap-2">
                  <span className="w-9 shrink-0 text-xs text-[var(--color-sub)]">{label}</span>
                  <input
                    type="text"
                    value={meals[key]}
                    onChange={(event) => {
                      const { value } = event.target;
                      setMeals((current) => ({ ...current, [key]: value }));
                    }}
                    placeholder={MEAL_PLACEHOLDERS[key]}
                    className={inputClass}
                  />
                </label>
                <label className="flex items-center gap-2 pl-11">
                  <input
                    type="number"
                    inputMode="numeric"
                    step="1"
                    value={calories[key]}
                    onChange={(event) => {
                      const { value } = event.target;
                      setCalories((current) => ({ ...current, [key]: value }));
                    }}
                    placeholder="例: 320"
                    className={`${inputClass} w-24`}
                  />
                  <span className="text-[11px] text-[var(--color-sub)]">kcal</span>
                </label>
                {!calorieValidOf(key) && (
                  <span className="pl-11 text-[11px] text-[var(--color-plum)]">
                    カロリーは0より大きく10000未満で入れてください。
                  </span>
                )}
              </div>
            ))}
          </div>

          <label className="block">
            <span className="mb-1 block text-[11px] text-[var(--color-sub)]">動いたこと(任意)</span>
            <input
              type="text"
              value={exercise}
              onChange={(event) => {
                setExercise(event.target.value);
              }}
              placeholder="例: 散歩 40分"
              className={inputClass}
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-[11px] text-[var(--color-sub)]">ひとこと(任意)</span>
            <textarea
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
              rows={2}
              placeholder="相手にも見えます"
              className={`${inputClass} resize-none`}
            />
          </label>
        </div>

        <div className="border-t border-[var(--color-line)] p-4">
          <button
            type="submit"
            disabled={!canSubmit || busy}
            className="w-full rounded-lg bg-[var(--color-accent)] py-2.5 text-sm font-medium text-white transition enabled:hover:opacity-85 disabled:opacity-50"
          >
            記録する
          </button>
        </div>
      </form>
    </>
  );
}
